import { doc, getDoc, setDoc } from 'firebase/firestore'
import { useEffect, useState } from 'react'
import { db } from '../../firebase'
import {
  Box,
  Button,
  Card,
  CardContent,
  Snackbar,
  Alert,
  Stack,
  TextField,
  Typography,
  Divider,
} from '@mui/material'

const APP_CONFIG_DOC = 'app_config'
const SETTINGS_COLLECTION = 'settings'

const defaults = {
  minAndroid: 106,
  latestAndroid: 106,
  minIos: 125,
  latestIos: 125,
  messageAndroid:
    'يتطلب التطبيق تحديثاً مهماً. يرجى التحديث من متجر Google Play لاستمرار الاستخدام.',
  messageIos:
    'يتطلب التطبيق تحديثاً مهماً. يرجى التحديث من App Store لاستمرار الاستخدام.',
}

function asInt(value: unknown, fallback: number): number {
  if (typeof value === 'number' && Number.isFinite(value)) return Math.trunc(value)
  if (typeof value === 'string') {
    const n = parseInt(value, 10)
    return Number.isFinite(n) ? n : fallback
  }
  return fallback
}

function asString(value: unknown, fallback: string): string {
  return typeof value === 'string' && value.trim() ? value : fallback
}

export default function AppSettingsPage() {
  const [minAndroid, setMinAndroid] = useState(defaults.minAndroid)
  const [latestAndroid, setLatestAndroid] = useState(defaults.latestAndroid)
  const [minIos, setMinIos] = useState(defaults.minIos)
  const [latestIos, setLatestIos] = useState(defaults.latestIos)
  const [messageAndroid, setMessageAndroid] = useState(defaults.messageAndroid)
  const [messageIos, setMessageIos] = useState(defaults.messageIos)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [snack, setSnack] = useState<{ open: boolean; msg: string; sev: 'success' | 'error' }>({
    open: false,
    msg: '',
    sev: 'success',
  })

  useEffect(() => {
    const load = async () => {
      try {
        const ref = doc(db, SETTINGS_COLLECTION, APP_CONFIG_DOC)
        const snap = await getDoc(ref)
        if (snap.exists()) {
          const d = snap.data()
          const legacyMin = asInt(d?.min_version_required, defaults.minAndroid)
          const legacyLatest = asInt(d?.latest_version, defaults.latestAndroid)
          const legacyMsg = asString(d?.force_update_message, defaults.messageAndroid)

          setMinAndroid(asInt(d?.min_version_required_android, legacyMin))
          setLatestAndroid(asInt(d?.latest_version_android, legacyLatest))
          setMinIos(asInt(d?.min_version_required_ios, legacyMin))
          setLatestIos(asInt(d?.latest_version_ios, legacyLatest))
          setMessageAndroid(asString(d?.force_update_message_android, legacyMsg))
          setMessageIos(
            asString(
              d?.force_update_message_ios,
              asString(d?.force_update_message, defaults.messageIos),
            ),
          )
        }
      } catch (e) {
        console.error('Error loading app_config:', e)
        setSnack({ open: true, msg: 'فشل تحميل الإعدادات', sev: 'error' })
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [])

  const handleSave = async () => {
    setSaving(true)
    try {
      const ref = doc(db, SETTINGS_COLLECTION, APP_CONFIG_DOC)
      await setDoc(
        ref,
        {
          // حقول منفصلة لكل منصة
          min_version_required_android: minAndroid,
          latest_version_android: latestAndroid,
          force_update_message_android: messageAndroid,
          min_version_required_ios: minIos,
          latest_version_ios: latestIos,
          force_update_message_ios: messageIos,
          // توافق مع نسخ قديمة من التطبيق (كانت تستخدم حقلاً واحداً = أندرويد)
          min_version_required: minAndroid,
          latest_version: latestAndroid,
          force_update_message: messageAndroid,
          updatedAt: new Date(),
        },
        { merge: true },
      )
      setSnack({ open: true, msg: 'تم حفظ إعدادات التطبيق بنجاح', sev: 'success' })
    } catch (e) {
      console.error('Error saving app_config:', e)
      setSnack({ open: true, msg: 'فشل الحفظ', sev: 'error' })
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography>جاري التحميل...</Typography>
      </Box>
    )
  }

  return (
    <Box sx={{ maxWidth: 720 }}>
      <Typography variant="h5" sx={{ mb: 1 }}>
        إعدادات إجبار التحديث
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        رقم البناء (مثل 106 لأندرويد أو 125 لآيفون) منفصل لكل منصة. زر التحديث في التطبيق يفتح المتجر الصحيح تلقائياً.
      </Typography>
      <Card>
        <CardContent>
          <Stack spacing={2}>
            <Typography variant="subtitle1" fontWeight={700}>
              أندرويد (Google Play)
            </Typography>
            <TextField
              label="الحد الإجباري لأندرويد"
              type="number"
              value={minAndroid}
              onChange={(e) => setMinAndroid(parseInt(e.target.value, 10) || 0)}
              helperText="إذا كان رقم بناء أندرويد أقل من هذا → شاشة إجبار ولا يدخل التطبيق."
              fullWidth
              inputProps={{ min: 0 }}
            />
            <TextField
              label="أحدث إصدار لأندرويد"
              type="number"
              value={latestAndroid}
              onChange={(e) => setLatestAndroid(parseInt(e.target.value, 10) || 0)}
              helperText="أقل من هذا وأعلى من الإجباري → نافذة اختيارية يمكن تخطيها."
              fullWidth
              inputProps={{ min: 0 }}
            />
            <TextField
              label="رسالة الإجبار (أندرويد)"
              value={messageAndroid}
              onChange={(e) => setMessageAndroid(e.target.value)}
              multiline
              rows={2}
              fullWidth
            />

            <Divider sx={{ my: 1 }} />

            <Typography variant="subtitle1" fontWeight={700}>
              آيفون (App Store)
            </Typography>
            <TextField
              label="الحد الإجباري لآيفون"
              type="number"
              value={minIos}
              onChange={(e) => setMinIos(parseInt(e.target.value, 10) || 0)}
              helperText="إذا كان رقم بناء آيفون أقل من هذا → شاشة إجبار ولا يدخل التطبيق."
              fullWidth
              inputProps={{ min: 0 }}
            />
            <TextField
              label="أحدث إصدار لآيفون"
              type="number"
              value={latestIos}
              onChange={(e) => setLatestIos(parseInt(e.target.value, 10) || 0)}
              helperText="أقل من هذا وأعلى من الإجباري → نافذة اختيارية يمكن تخطيها."
              fullWidth
              inputProps={{ min: 0 }}
            />
            <TextField
              label="رسالة الإجبار (آيفون)"
              value={messageIos}
              onChange={(e) => setMessageIos(e.target.value)}
              multiline
              rows={2}
              fullWidth
            />

            <Button variant="contained" onClick={handleSave} disabled={saving}>
              {saving ? 'جاري الحفظ...' : 'حفظ'}
            </Button>
          </Stack>
        </CardContent>
      </Card>
      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snack.sev} onClose={() => setSnack((s) => ({ ...s, open: false }))}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  )
}
